import React, { FC, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { useSelector, useDispatch } from "react-redux";

import TopHeader from "./components/TopHeader/TopHeader";
import MiniCartItem from "./components/MiniCartItem/MiniCartItem";
import EmptyCartView from "./components/EmptyCartView/EmptyCartView";
import BottomActions from "./components/BottomActions/BottomActions";

import { useDebounceCartUpdates } from "../../hooks/useDebounceCartUpdates";
import {
  cleanupStaleOptimisticUpdates,
  removeOptimisticProduct,
} from "../../redux/cartReducer";
import { calculateItemsSubtotal } from "../../utils/cartCalculationUtils";

import styles from "./MiniCart.module.css";
import { lockBodyScroll, unlockBodyScroll } from "../../utils/scrollUtils";

type Props = { toggleSidenav: () => void };

const MiniCart: FC<Props> = ({ toggleSidenav }) => {
  const [isVisible, setIsVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use Redux store for cart data
  const cart = useSelector((state: any) => state.cart);
  const { groups, optimisticProducts, totalPrice: cartTotalPrice } = cart;

  // Use the shared debounce hook for cart updates
  const {
    handleIncrease,
    handleDecrease,
    handleRemove,
    getOptimisticQuantity,
    getOptimisticPrice,
    getOptimisticTotalPrice,
    flushUpdates,
    hasPendingUpdates,
  } = useDebounceCartUpdates(1000);

  // Cleanup function that runs on mount and periodically
  const cleanupStaleUpdates = useCallback(() => {
    if (optimisticProducts?.length > 0) {
      console.log(
        `🔍 Checking ${optimisticProducts.length} optimistic updates for stale entries`
      );
      dispatch(cleanupStaleOptimisticUpdates());
    }
  }, [dispatch, optimisticProducts?.length]);

  // Run cleanup when component mounts and whenever optimisticProducts changes
  useEffect(() => {
    cleanupStaleUpdates();

    // Also set up a periodic cleanup for long-lived components
    const intervalId = setInterval(cleanupStaleUpdates, 10000); // Check every 10 seconds

    // Flush any pending updates when component unmounts
    return () => {
      clearInterval(intervalId);
      if (hasPendingUpdates()) {
        flushUpdates();
      }
    };
  }, [cleanupStaleUpdates, flushUpdates, hasPendingUpdates]);

  // Combine regular and optimistic products
  const cartItems = useMemo(
    () => [
      // Regular products from cart groups
      ...groups.flatMap((group: any) =>
        group.products
          .filter((product: any) => product.quantity > 0) // Filter out zero quantity items
          .map((product: any) => ({
            ...product,
            groupId: group.id,
            isOptimistic: false,
          }))
      ),
      // Add any optimistic products that might not be in groups yet
      ...(optimisticProducts || []).map((item: any) => ({
        ...item.product,
        operationId: item.operationId,
        timestamp: item.timestamp,
        isOptimistic: true,
      })),
    ],
    [groups, optimisticProducts]
  );

  // Deduplicate items if necessary (in case optimistic updates get double-counted)
  const deduplicatedCartItems = useMemo(() => {
    const dedupMap = new Map();

    // First add all confirmed items
    cartItems
      .filter((item) => !item.isOptimistic)
      .forEach((item) => {
        dedupMap.set(item.cartCustomId, item);
      });

    // Then add optimistic items only if there's no confirmed version
    cartItems
      .filter((item) => item.isOptimistic)
      .forEach((item) => {
        if (!dedupMap.has(item.cartCustomId)) {
          dedupMap.set(item.cartCustomId, item);
        }
      });

    return Array.from(dedupMap.values());
  }, [cartItems]);

  const totalQuantity = useMemo(
    () =>
      deduplicatedCartItems.reduce((acc: number, item: any) => {
        const qty = getOptimisticQuantity(item.cartCustomId, item.quantity);
        return acc + qty;
      }, 0),
    [deduplicatedCartItems, getOptimisticQuantity]
  );

  // Calculate subtotal using the cart calculation utility function
  const subtotal = useMemo(() => {
    // Create an array with optimistic quantities for all items
    const optimisticItems = deduplicatedCartItems.map((item) => ({
      ...item,
      quantity: getOptimisticQuantity(item.cartCustomId, item.quantity),
    }));

    // Filter out any items with zero quantity
    const nonZeroItems = optimisticItems.filter((item) => item.quantity > 0);

    // Calculate the subtotal using the utility function
    return calculateItemsSubtotal(nonZeroItems);
  }, [deduplicatedCartItems, getOptimisticQuantity]);

  // Add a flag to mark if there are optimistic updates or pending changes
  const hasOptimisticUpdates = useMemo(() => {
    return (
      hasPendingUpdates() ||
      deduplicatedCartItems.some((item) => item.isOptimistic)
    );
  }, [deduplicatedCartItems, hasPendingUpdates]);

  const handleToggleSidenav = () => {
    // Flush any pending updates before closing the mini-cart
    if (hasPendingUpdates()) {
      flushUpdates();
    }

    setIsVisible(false);
    unlockBodyScroll();
    toggleSidenav();
  };

  // Calculate how long an optimistic product has been in the cart
  const getOptimisticAgeSeconds = (timestamp: number) => {
    if (!timestamp) return 0;
    return Math.round((Date.now() - timestamp) / 1000);
  };

  useEffect(() => {
    setIsVisible(true);
    lockBodyScroll();

    // Cleanup function
    return () => {
      unlockBodyScroll();
    };
  }, []);

  return (
    <div>
      <div className={styles.overlay} onClick={handleToggleSidenav}></div>
      <div
        className={`${styles.miniCart} ${
          isVisible ? styles.visible : styles.hidden
        }`}
      >
        <TopHeader toggle={handleToggleSidenav} total={totalQuantity} />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            paddingBottom: deduplicatedCartItems.length ? "207px" : "75px",
          }}
        >
          {deduplicatedCartItems.length > 0 ? (
            deduplicatedCartItems.map((item: any) => (
              <MiniCartItem
                key={`${item.cartCustomId}${item.isOptimistic ? "-opt" : ""}`}
                item={{
                  ...item,
                  // Pass how old the optimistic update is
                  optimisticAge:
                    item.isOptimistic && item.timestamp
                      ? getOptimisticAgeSeconds(item.timestamp)
                      : 0,
                }}
                handleIncrease={() => handleIncrease(item)}
                handleDecrease={() => handleDecrease(item)}
                handleRemove={() => handleRemove(item)}
                getOptimisticQuantity={(defaultQty) =>
                  getOptimisticQuantity(item.cartCustomId, defaultQty)
                }
                getOptimisticPrice={(defaultPrice) =>
                  getOptimisticPrice(item.cartCustomId, defaultPrice)
                }
                getOptimisticTotalPrice={(defaultPrice, qty) =>
                  getOptimisticTotalPrice(item.cartCustomId, defaultPrice, qty)
                }
                toggleSidenav={handleToggleSidenav}
                handleRemoveOptimistic={
                  item.isOptimistic
                    ? () =>
                        dispatch(
                          removeOptimisticProduct({
                            cartCustomId: item.cartCustomId,
                            operationId: item.operationId,
                          })
                        )
                    : undefined
                }
              />
            ))
          ) : (
            <EmptyCartView />
          )}
        </Box>

        {deduplicatedCartItems.length > 0 && (
          <div className={styles.bottomActionsContainer}>
            <BottomActions
              subtotal={`S/ ${subtotal.toFixed(2)}`}
              hasOptimisticItems={hasOptimisticUpdates}
              handleNavigate={navigate}
              toggleMiniCart={handleToggleSidenav}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniCart;
